import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getApiCall } from '@/service/httpCall';

export type LeadPageFilters = {
  status?: string;
  search?: string;
  from_date?: string;
  to_date?: string;
};

export function usePagedLeads(url: string | null, filters: LeadPageFilters = {}, limit = 25) {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const reqId = useRef(0);

  const { status = '', search = '', from_date = '', to_date = '' } = filters;
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => setPage(1), [url, status, debouncedSearch, from_date, to_date]);

  const load = useCallback(() => {
    if (!url) return;
    const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (status && status !== 'all') qs.set('status', status);
    if (debouncedSearch.trim()) qs.set('search', debouncedSearch.trim());
    if (from_date) qs.set('from_date', from_date);
    if (to_date) qs.set('to_date', to_date);
    const id = ++reqId.current;
    setLoading(true);
    getApiCall(`${url}?${qs}`, {})
      .then((resp: any) => {
        if (id !== reqId.current) return; // a newer request superseded this one
        setRows(Array.isArray(resp?.rows) ? resp.rows : []);
        setTotal(Number(resp?.total) || 0);
        setError(null);
      })
      .catch((err: any) => id === reqId.current && setError(err))
      .finally(() => id === reqId.current && setLoading(false));
  }, [url, page, limit, status, debouncedSearch, from_date, to_date]);

  useEffect(load, [load]);

  return { rows, total, page, setPage, limit, loading, error, reload: load };
}

export function LeadsPager({
  page,
  total,
  limit,
  onPage,
}: {
  page: number;
  total: number;
  limit: number;
  onPage: (p: number) => void;
}) {
  if (total <= limit) return null;
  const pages = Math.ceil(total / limit);
  const from = (page - 1) * limit + 1;
  const to = Math.min(page * limit, total);
  return (
    <div className="flex items-center justify-between gap-2 px-3 py-2 text-xs text-muted-foreground">
      <span>
        {from.toLocaleString('en-IN')}–{to.toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}
      </span>
      <div className="flex items-center gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="Previous page">
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-1">
          {page} / {pages}
        </span>
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="Next page">
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
